import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CubeIcon,
  FileIcon,
  FilePdfIcon,
  FileTextIcon,
} from "@phosphor-icons/react";
import { Fragment, type ReactNode, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";

// Écran 003 « Classeur OS » : chaque OS, les FTM dont il confirme la commande,
// puis les documents confirmés dans le tableau du classeur FTM.

export type OrdreServiceItem =
  paths["/api/yards/{yardId}/ordres-service"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

type FetchState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; items: OrdreServiceItem[] };

const COLUMN_COUNT = 10;
const cellClass = "truncate px-2 text-center";
const mergedCellClass = "px-2 text-center align-middle";

function formatDate(iso: string | null): string {
  return isNil(iso) ? "-" : formatDay(iso);
}

function ftmLabel(ftm: OrdreServiceItem["ftms"][number]): string {
  return isNil(ftm.dateRedaction)
    ? `FTM n°${ftm.number} - ${ftm.object}`
    : `FTM n°${ftm.number} - ${ftm.object}, rédigée le ${formatDate(ftm.dateRedaction)}`;
}

export function OSClasseur() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // Saving an OS from the actions drawer navigates here: a new location key
  // refetches the list.
  const { key: locationKey } = useLocation();

  useEffect(() => {
    async function fetchData() {
      try {
        const { data } = await apiClient.GET(
          "/api/yards/{yardId}/ordres-service",
          { params: { path: { yardId: yardId ?? "" } } },
        );
        setState(
          isNotNil(data)
            ? { status: "success", items: data.data.items }
            : { status: "error" },
        );
      } catch (error) {
        console.error("Failed to load ordres de service", error);
        setState({ status: "error" });
      }
    }
    fetchData();
  }, [yardId, locationKey]);

  async function openFichier(fichierId: number) {
    try {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        {
          params: {
            path: { yardId: yardId ?? "", fichierId: String(fichierId) },
          },
        },
      );
      if (isNotNil(data)) {
        window.open(data.data.inlineUrl, "_blank");
        return;
      }
    } catch (error) {
      console.error("Failed to open fichier", error);
    }
    toaster.error({
      title: "Erreur",
      description: "Impossible d'ouvrir le fichier",
    });
  }

  const items = state.status === "success" ? state.items : [];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className="shrink-0 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Classeur OS
        </h2>
        {state.status === "success" && (
          <p className="mt-1 text-sm text-zinc-500">
            {items.length > 1
              ? `${items.length} ordres de service`
              : `${items.length} ordre de service`}
          </p>
        )}
      </header>

      <div className="flex min-h-0 flex-1 flex-col px-4 py-4">
        {state.status === "loading" ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Chargement des ordres de service…
          </p>
        ) : state.status === "error" ? (
          <p className="py-12 text-center text-sm text-red-600">
            Impossible de charger les ordres de service.
          </p>
        ) : items.length === 0 ? (
          <p className="py-12 text-center text-sm text-zinc-500">
            Aucun ordre de service
          </p>
        ) : (
          <div className="min-h-0 overflow-y-auto overflow-x-hidden rounded-lg border border-zinc-200 dark:border-zinc-700">
            <table className="w-full table-fixed border-collapse text-sm">
              {/* Widths: .agents/screen-column-widths.md, « Écran 003 ».
                  Variable columns take their P95 character count as a
                  percentage: Chrome and Safari shrink percentage columns
                  into the space the fixed columns leave, proportionally.
                  A calc() mixing % and rem on <col> is ignored by both,
                  which splits that space evenly. */}
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "25%" }} />
                <col className="w-24" />
                <col className="w-24" />
                <col style={{ width: "20%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "30%" }} />
                <col className="w-16" />
                <col className="w-28" />
                <col className="w-12" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800">
                <tr className="h-8 border-b border-zinc-200 dark:border-zinc-700">
                  <th colSpan={4} className="px-2 text-center font-medium">
                    FTM
                  </th>
                  <th colSpan={6} className="px-2 text-center font-medium">
                    Documents et/ou maquettes confirmés
                  </th>
                </tr>
                <tr className="h-8 border-b border-zinc-200 dark:border-zinc-700">
                  <th className="px-2 text-center font-medium">N° FTM</th>
                  <th className="px-2 text-center font-medium">Objet</th>
                  <th
                    className="px-2 text-center font-medium"
                    title="Date de réception"
                  >
                    Date récep.
                  </th>
                  <th
                    className="px-2 text-center font-medium"
                    title="Date de rédaction"
                  >
                    Date rédac.
                  </th>
                  <th className="px-2 text-center font-medium">Auteur</th>
                  <th className="px-2 text-center font-medium">N°</th>
                  <th className="px-2 text-center font-medium">Intitulé</th>
                  <th className="px-2 text-center font-medium">Indice</th>
                  <th className="px-2 text-center font-medium">Fichiers</th>
                  <th className="px-2 text-center font-medium">BIM</th>
                </tr>
              </thead>
              <tbody>
                {items.map((os) => (
                  <OrdreServiceRows
                    key={os.id}
                    os={os}
                    onOpenFichier={openFichier}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FichierButton({
  fichierId,
  label,
  icon,
  onOpen,
}: {
  fichierId: number | null;
  label: string;
  icon: ReactNode;
  onOpen: (fichierId: number) => void;
}) {
  if (isNil(fichierId)) return null;
  return (
    <button
      type="button"
      onClick={() => onOpen(fichierId)}
      aria-label={label}
      title={label}
      className="inline-flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400"
    >
      {icon}
    </button>
  );
}

function OrdreServiceRows({
  os,
  onOpenFichier,
}: {
  os: OrdreServiceItem;
  onOpenFichier: (fichierId: number) => void;
}) {
  const osLabel = `OS n°${os.number} du ${formatDate(os.dateOs)} relatif à :`;
  const confirmed = os.ftms.filter((ftm) => ftm.documents.length > 0);

  return (
    <>
      <tr className="h-10 border-b border-zinc-200 bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800">
        <td colSpan={COLUMN_COUNT} className="px-2">
          <div
            className="flex items-center gap-2 font-semibold"
            title={osLabel}
          >
            <FichierButton
              fichierId={os.fichierId}
              label={`Ouvrir le fichier de l'OS n°${os.number}`}
              icon={<FileIcon size={18} />}
              onOpen={onOpenFichier}
            />
            <span className="truncate">{osLabel}</span>
          </div>
        </td>
      </tr>
      {os.ftms.map((ftm) => {
        const label = ftmLabel(ftm);
        return (
          <tr key={`ftm-${ftm.id}`} className="h-8">
            <td colSpan={COLUMN_COUNT} className="pr-2 pl-6">
              <div className="flex items-center gap-2" title={label}>
                {isNotNil(ftm.fichierId) ? (
                  <FichierButton
                    fichierId={ftm.fichierId}
                    label={`Ouvrir le fichier de la FTM n°${ftm.number}`}
                    icon={<FileTextIcon size={16} />}
                    onOpen={onOpenFichier}
                  />
                ) : (
                  <FileTextIcon size={16} className="text-zinc-300" />
                )}
                <span className="truncate">{label}</span>
              </div>
            </td>
          </tr>
        );
      })}
      {confirmed.length === 0 ? (
        <tr className="h-10 border-b border-zinc-200 dark:border-zinc-700">
          <td colSpan={COLUMN_COUNT} className="px-2 text-center text-zinc-500">
            Aucun document confirmé
          </td>
        </tr>
      ) : (
        confirmed.map((ftm) => (
          <Fragment key={`docs-${ftm.id}`}>
            {ftm.documents.map((doc, index) => (
              <tr
                key={doc.id}
                className="h-10 border-b border-zinc-100 dark:border-zinc-800"
              >
                {index === 0 && (
                  <>
                    <td
                      rowSpan={ftm.documents.length}
                      className={mergedCellClass}
                    >
                      <div className="truncate" title={ftm.number}>
                        {ftm.number}
                      </div>
                    </td>
                    <td
                      rowSpan={ftm.documents.length}
                      className={mergedCellClass}
                    >
                      <div className="truncate" title={ftm.object}>
                        {ftm.object}
                      </div>
                    </td>
                    <td
                      rowSpan={ftm.documents.length}
                      className={mergedCellClass}
                    >
                      {formatDate(ftm.dateReception)}
                    </td>
                    <td
                      rowSpan={ftm.documents.length}
                      className="border-r border-zinc-100 px-2 text-center align-middle dark:border-zinc-800"
                    >
                      {formatDate(ftm.dateRedaction)}
                    </td>
                  </>
                )}
                <td className={cellClass} title={doc.author ?? undefined}>
                  {doc.author ?? "-"}
                </td>
                <td className={cellClass} title={doc.number}>
                  {doc.number}
                </td>
                <td className={cellClass} title={doc.title}>
                  {doc.title}
                </td>
                <td className={cellClass}>{doc.indice ?? "-"}</td>
                <td className="px-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <FichierButton
                      fichierId={doc.fichierPdfId}
                      label={`Ouvrir le PDF du document ${doc.number}`}
                      icon={<FilePdfIcon size={18} />}
                      onOpen={onOpenFichier}
                    />
                    <FichierButton
                      fichierId={doc.fichierNativeId}
                      label={`Ouvrir le fichier natif du document ${doc.number}`}
                      icon={<FileIcon size={18} />}
                      onOpen={onOpenFichier}
                    />
                    {isNil(doc.fichierPdfId) && isNil(doc.fichierNativeId) && (
                      <span className="text-zinc-300">-</span>
                    )}
                  </div>
                </td>
                <td className="px-2 text-center">
                  {doc.bimAvailable ? (
                    <CubeIcon
                      size={18}
                      className="inline text-emerald-600"
                      aria-label="Maquette disponible"
                    />
                  ) : (
                    <span className="text-zinc-300">-</span>
                  )}
                </td>
              </tr>
            ))}
          </Fragment>
        ))
      )}
    </>
  );
}
